import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

import LoginLayout from "../../layouts/LoginLayout";
import { useNavigate } from "react-router-dom";
import { InputReactHookForm } from "../../components/input/InputReactHookForm";
import alertService from "../../components/alert/alertService";
import UsersService from "../../service/users.service";
import { useAuth } from "../../contexts/UserContext";

import Link from "@mui/material/Link";
import { common } from "../../i18n";
import { EMAIL_REGEX } from "../../constants/form";
import { LOGO_IMG_SRC } from "../../constants";

type FormValues = {
  email: string;
};

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation([common]);

  const navigate = useNavigate();

  const { logout } = useAuth();

  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
  } = useForm<FormValues>({
    mode: "all",
    reValidateMode: "onChange",
    delayError: 400,
  });

  const onSubmit = async (values: FormValues) => {
    let result;
    try {
      result = await UsersService.forgetPassword(values.email);
    } catch {
      // error alert already shown by service
      return;
    }

    if (result) {
      await alertService.confirm(
        t("Successfully"),
        <Trans
          t={t}
          i18nKey="User-Locked-Check-Your-Email"
          components={{
            b: <span style={{ fontWeight: 600, color: "var(--red)" }} />,
          }}
        />,
        t("Close"),
        "var(--dark)",
        true,
        undefined,
        undefined,
        undefined,
      );
      // t("User-Locked-Check-Your-Email")

      logout();

      navigate("/login");
    }
  };

  return (
    <LoginLayout>
      <Box
        maxWidth={"400px"}
        display={"flex"}
        flexDirection={"column"}
        rowGap={"20px"}
        width={"100%"}
      >
        <img
          src={LOGO_IMG_SRC}
          alt="logo"
          style={{
            padding: "12px 0px",
            height: "28px",
            width: "233px",
            marginBottom: "20px",
          }}
        />

        <Typography variant="h1">{t("Forgot-Password")}</Typography>

        <Typography variant="text1">
          {t("Enter-Your-Email-To-Reset-Password")}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              width: "100%",
            }}
            padding={"24px 0px"}
          >
            <InputReactHookForm
              required
              rules={{
                required: t("Must-be-a-valid-email"),
                pattern: {
                  value: EMAIL_REGEX,
                  message: t("Must-be-a-valid-email"),
                },
              }}
              control={control}
              name={"email"}
              label={t("Email")}
              info={t("Email")}
              placeholder={t("Email")}
              type="text"
            />
          </Box>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            color={"secondary"}
            disabled={!isValid || isSubmitting}
            sx={{
              marginBottom: "20px",
            }}
          >
            {t("Send")}
          </Button>

          <Link variant="body1" href="/login" underline="hover" color="primary">
            {t("Go-To-Login-Page")}
          </Link>
        </form>
      </Box>
    </LoginLayout>
  );
};

export default ForgotPasswordPage;
